import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { Observable } from 'rxjs';
import { ErrorResponse } from '@core/error/error-response.model';
import { LOCALIZATION_CONFIG, LocalizationConfig } from '@core/localization/localization.config';
import { TranslateService } from '@ngx-translate/core';
import { AvailableLanguage, toHtmlLang } from '@core/localization/available-language';
import { CookieService } from 'ngx-cookie-service';
import { StorageKeys } from '@core/storage/storage.keys';
import { environment } from '@environments/environment';
import { RouteEntity } from '@core/router/route.entity';
import { filter, tap } from 'rxjs/operators';
import { RoamingService } from '@api/services/roaming.service';
import { Language } from '@api/models/Postgres/Model/language';
import { Language as LanguageUser } from '@api/models/UserService/Contract/language';
import { UsersService } from '@api/services/users.service';
import { CustomRxState } from '@core/state/rx.state';
import { SettingsService } from '@core/settings';


export interface ILanguageState {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    languages: Language[];
    userLanguages: LanguageUser[];
    language: AvailableLanguage;
}

export interface ILanguageAction {
    requestLanguages: Language[];
    requestUserLanguages: LanguageUser[];
    setCurrentLanguage: AvailableLanguage;
}

@Injectable({
    providedIn: 'root',
})
export class LanguageState extends CustomRxState<ILanguageState> {
    readonly #actions: RxActions<ILanguageAction> = new RxActionFactory<ILanguageAction>().create();
    readonly #localizationConfig: LocalizationConfig = inject(LOCALIZATION_CONFIG);
    readonly #translate: TranslateService = inject(TranslateService);
    readonly #cookieService: CookieService = inject(CookieService);
    readonly #settingsService: SettingsService = inject(SettingsService);
    readonly #roamingService: RoamingService = inject(RoamingService);
    readonly #usersService: UsersService = inject(UsersService);
    readonly #document: Document = inject(DOCUMENT);

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly languages$: Observable<Language[]> = this.select('languages');
    public readonly userLanguages$: Observable<LanguageUser[]> = this.select('userLanguages');
    public readonly language$: Observable<AvailableLanguage> = this.select('language');


    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.#translate.setFallbackLang(this.#localizationConfig.defaultLocale);
    }

    public requestLanguages(): void {
        this.hold(
            this.#roamingService.apiRoamingListLanguagesGet(),
            this.#actions.requestLanguages,
        );
    }

    public requestUserLanguages(): void {
        this.hold(
            this.#usersService.apiUsersListLanguagesGet(),
            this.#actions.requestUserLanguages,
        );
    }

    public setCurrentLanguage(language: Language): void {
        const isoCode: string = language.code?.toLowerCase();
        const cookieDomain: string = environment.isLocalhost ? null : this.#settingsService.settings.APP_SETTINGS_BASE_DOMAIN;

        this.#translate.use(isoCode);
        this.#document.documentElement.lang = toHtmlLang(isoCode);
        this.#cookieService.set(StorageKeys.CookieLang, isoCode, null, RouteEntity.Root, cookieDomain);
        this.#actions.setCurrentLanguage(isoCode as AvailableLanguage);
    }

    public get currentLang(): AvailableLanguage {
        return this.get('language') || this.#localizationConfig.defaultLocale;
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            languages: null,
        });
    }

    private connectSelectors(): void {
        this.connect('languages', this.#actions.requestLanguages$.pipe(
            filter(Boolean),
            tap((languages: Language[]) => {
                const cookieLang: string = this.#cookieService.get(StorageKeys.CookieLang);
                const cookieLangInDb: Language = languages?.find((lang: Language) => lang.code.toLowerCase() === cookieLang);

                this.setCurrentLanguage(cookieLang && cookieLangInDb ? cookieLangInDb : languages[0]);
            }),
        ));

        this.connect('language', this.#actions.setCurrentLanguage$);
        this.connect('userLanguages', this.#actions.requestUserLanguages$);
    }
}
